import React, { useState } from "react";
import Search from "@mui/icons-material/Search";
import MoreHoriz from "@mui/icons-material/MoreHoriz";
import CheckCircleOutline from "@mui/icons-material/CheckCircleOutline";
import TrendingUp from "@mui/icons-material/TrendingUp";
import Tune from "@mui/icons-material/Tune";
import BarChart from "@mui/icons-material/BarChart";
import ScienceOutlined from "@mui/icons-material/ScienceOutlined";
import PeopleOutline from "@mui/icons-material/PeopleOutline";
import CampaignOutlined from "@mui/icons-material/CampaignOutlined";
import AppIcon from "./DemoAppIcon";

const number = new Intl.NumberFormat("en-US");
const money = new Intl.NumberFormat("en-US", {
  style: "currency",
  currency: "USD",
  maximumFractionDigits: 0,
});

function PostHogScene({ mission, stage }) {
  const rollout = stage === mission.steps.length - 1;
  const [metric, setMetric] = useState("users");
  const latest = mission.weeklyUsers[mission.weeklyUsers.length - 1];
  const baseline = mission.weeklyUsers[0][metric];
  const increase = ((latest[metric] / baseline - 1) * 100).toFixed(1);
  const metricLabel =
    metric === "users" ? "Weekly active users" : "Weekly returning users";
  return (
    <div className="work-posthog">
      <aside>
        <strong className="cg-app-brand">
          <AppIcon id="posthog" />
          PostHog
        </strong>
        <span className="cg-project">
          Northstar <small>Production</small>
        </span>
        <span>
          <Search />
          Search
        </span>
        <span className="active">
          <BarChart />
          Product analytics
        </span>
        <span>Web analytics</span>
        <span>Session replay</span>
        <span>
          <ScienceOutlined />
          Experiments
        </span>
        <span>Feature flags</span>
        <span>
          <PeopleOutline />
          People & groups
        </span>
        <span className="work-side-label">Pinned</span>
        <span>Growth dashboard</span>
        <span>Activation funnel</span>
      </aside>
      <div className="cg-posthog-main">
        <div className="cg-breadcrumb">
          Product analytics <span>/</span>{" "}
          {rollout ? "Rollout decision" : "Growth dashboard"} <MoreHoriz />
        </div>
        <section className="cg-insight">
          <header>
            <div>
              <h4>{metricLabel}</h4>
              <p>Unique users / workflow_completed / Weekly</p>
            </div>
            <span className="cg-date">Jul 27 - Sep 4</span>
          </header>
          <div
            className="cg-insight-tabs"
            role="group"
            aria-label="Growth insight"
          >
            <button
              aria-pressed={metric === "users"}
              onClick={() => setMetric("users")}
            >
              Active users
            </button>
            <button
              aria-pressed={metric === "returning"}
              onClick={() => setMetric("returning")}
            >
              Returning users
            </button>
            <span>
              <CheckCircleOutline />
              Saved insight
            </span>
          </div>
          <div className="cg-growth-metrics">
            <div>
              <span>
                {metric === "users" ? "Weekly active" : "Weekly returning"}
              </span>
              <strong>{number.format(latest[metric])}</strong>
            </div>
            <div>
              <span>Growth from baseline</span>
              <strong>+{increase}%</strong>
            </div>
            <div>
              <span>First-workflow activation</span>
              <strong>
                56.8% <small>from 42%</small>
              </strong>
            </div>
          </div>
          <div
            className="cg-usage-chart"
            role="img"
            aria-label={
              metricLabel +
              ": " +
              mission.weeklyUsers
                .map(
                  (point) => point.week + ", " + number.format(point[metric]),
                )
                .join("; ")
            }
          >
            <div className="cg-chart-axis">
              <span>7k</span>
              <span>3.5k</span>
              <span>0</span>
            </div>
            <div className="cg-chart-bars">
              {mission.weeklyUsers.map((point, i) => (
                <div className="cg-chart-column" key={point.week}>
                  <div
                    className={
                      "cg-chart-bar " +
                      (metric === "returning" ? "returning" : "")
                    }
                    style={{ height: (point[metric] / 7000) * 100 + "%" }}
                  >
                    <span>{number.format(point[metric])}</span>
                  </div>
                  <small>
                    {point.week}
                    {i === 5 ? "*" : ""}
                  </small>
                </div>
              ))}
            </div>
          </div>
          <div className="cg-insight-foot">
            <span>* Current week in progress</span>
            <span>Mission target: 10,000 weekly active users</span>
          </div>
          <div className="cg-experiment-result">
            <ScienceOutlined />
            <div>
              <strong>
                {rollout
                  ? "Guided first workflow / Next rollout"
                  : "First-workflow activation gap"}
              </strong>
              <span>
                {rollout
                  ? "56.8% activation / monitor retention as rollout expands"
                  : "42% baseline activation / 58% do not reach first value"}
              </span>
            </div>
            <b>{rollout ? "Ready to expand" : "Investigate"}</b>
          </div>
        </section>
      </div>
    </div>
  );
}

function GoogleAdsScene({ mission }) {
  const totals = mission.campaigns.reduce(
    (sum, campaign) => ({
      clicks: sum.clicks + campaign.clicks,
      trials: sum.trials + campaign.trials,
      cost: sum.cost + campaign.cost,
    }),
    { clicks: 0, trials: 0, cost: 0 },
  );
  return (
    <div className="work-googleads">
      <header className="cg-ads-header">
        <div className="cg-app-brand">
          <AppIcon id="googleads" />
          <strong>Google Ads</strong>
        </div>
        <div className="cg-ads-search">
          <Search />
          Search campaigns
        </div>
        <span>Northstar</span>
        <b>N</b>
      </header>
      <div className="cg-ads-layout">
        <aside>
          <span>
            <BarChart />
            Overview
          </span>
          <span className="active">
            <CampaignOutlined />
            Campaigns
          </span>
          <span>Ad groups</span>
          <span>Ads</span>
          <span>Keywords</span>
          <span>Audiences</span>
          <span>
            <TrendingUp />
            Goals
          </span>
          <span className="work-side-label">Conversion goal</span>
          <strong className="cg-conversion-name">Qualified trial</strong>
        </aside>
        <section>
          <header>
            <div>
              <h4>Campaigns</h4>
              <p>Search / Qualified trial acquisition</p>
            </div>
            <span className="cg-date">Aug 25 - Sep 4</span>
          </header>
          <div className="cg-ads-filters">
            <Tune />
            <span>All enabled campaigns</span>
            <span>Conversion: Qualified trial</span>
          </div>
          <div className="cg-ads-metrics">
            <div>
              <span>Clicks</span>
              <strong>{number.format(totals.clicks)}</strong>
            </div>
            <div>
              <span>Qualified trials</span>
              <strong>{totals.trials}</strong>
            </div>
            <div>
              <span>Cost / trial</span>
              <strong>${(totals.cost / totals.trials).toFixed(2)}</strong>
            </div>
            <div>
              <span>Cost</span>
              <strong>{money.format(totals.cost)}</strong>
            </div>
          </div>
          <table className="cg-campaign-table">
            <thead>
              <tr>
                <th>Campaign</th>
                <th>Status</th>
                <th>Clicks</th>
                <th>Trials</th>
                <th>Cost</th>
                <th>Cost / trial</th>
              </tr>
            </thead>
            <tbody>
              {mission.campaigns.map((campaign) => (
                <tr key={campaign.name}>
                  <td>
                    <i />
                    {campaign.name}
                  </td>
                  <td>Eligible</td>
                  <td>{number.format(campaign.clicks)}</td>
                  <td>{campaign.trials}</td>
                  <td>{money.format(campaign.cost)}</td>
                  <td>${(campaign.cost / campaign.trials).toFixed(2)}</td>
                </tr>
              ))}
            </tbody>
            <tfoot>
              <tr>
                <td>Total: search campaigns</td>
                <td />
                <td>{number.format(totals.clicks)}</td>
                <td>{totals.trials}</td>
                <td>{money.format(totals.cost)}</td>
                <td>${(totals.cost / totals.trials).toFixed(2)}</td>
              </tr>
            </tfoot>
          </table>
          <div className="cg-ads-budget">
            <CheckCircleOutline />
            <div>
              <strong>Within the delegated paid-media budget</strong>
              <span>
                {money.format(totals.cost)} used /{" "}
                {money.format(mission.paidMediaCap)} cap
              </span>
            </div>
            <div className="cg-budget-track">
              <i
                style={{
                  width: (totals.cost / mission.paidMediaCap) * 100 + "%",
                }}
              />
            </div>
          </div>
          <p className="cg-ads-attribution">
            122 paid-search trials. Email and organic results are reconciled in
            the campaign scorecard.
          </p>
        </section>
      </div>
    </div>
  );
}

function MailchimpScene() {
  const [view, setView] = useState("report");
  return (
    <div className="work-mailchimp">
      <aside>
        <strong className="cg-app-brand">
          <AppIcon id="mailchimp" />
          Mailchimp
        </strong>
        <span className="cg-project">Northstar</span>
        <span>Home</span>
        <span className="active">Campaigns</span>
        <span>Automations</span>
        <span>Audience</span>
        <span>Analytics</span>
        <span>Content</span>
        <span>Integrations</span>
        <span className="work-side-label">Workspace</span>
        <span>Marketing team</span>
      </aside>
      <section>
        <div className="cg-breadcrumb">
          Campaigns / Product launch <MoreHoriz />
        </div>
        <header>
          <div>
            <h4>Your first workflow starts here</h4>
            <p>Email campaign / Product launch / September 3, 2026</p>
          </div>
          <span className="cg-sent">Sent</span>
        </header>
        <div
          className="cg-mail-tabs"
          role="group"
          aria-label="Marketing email view"
        >
          <button
            aria-pressed={view === "report"}
            onClick={() => setView("report")}
          >
            Report
          </button>
          <button
            aria-pressed={view === "email"}
            onClick={() => setView("email")}
          >
            View email
          </button>
          <span>Bot filtering on</span>
        </div>
        {view === "report" ? (
          <div className="cg-mail-report">
            <div className="cg-mail-metrics">
              <div>
                <span>Delivered</span>
                <strong>2,400</strong>
              </div>
              <div>
                <span>Clicked</span>
                <strong>320</strong>
              </div>
              <div>
                <span>Click rate</span>
                <strong>13.3%</strong>
              </div>
              <div>
                <span>Unsubscribed</span>
                <strong>6</strong>
              </div>
            </div>
            <div className="cg-audience">
              <PeopleOutline />
              <div>
                <strong>Founders and operations teams</strong>
                <span>Opted-in audience / Product updates</span>
              </div>
              <span>2,406 recipients</span>
            </div>
            <h5>Top links clicked</h5>
            <div className="cg-mail-clicks">
              <span>Start your first workflow</span>
              <i>
                <b style={{ width: "80%" }} />
              </i>
              <strong>256</strong>
            </div>
            <div className="cg-mail-clicks">
              <span>See the customer guide</span>
              <i>
                <b style={{ width: "20%" }} />
              </i>
              <strong>64</strong>
            </div>
            <div className="cg-mail-followup">
              <CheckCircleOutline />
              <div>
                <strong>Campaign tracking connected</strong>
                <span>
                  64 qualified trials attributed to email in the shared
                  scorecard.
                </span>
              </div>
            </div>
          </div>
        ) : (
          <article className="cg-email-preview">
            <span className="cg-email-brand">Northstar</span>
            <span className="cg-email-eyebrow">A BETTER FIRST DAY</span>
            <h5>
              From a new workspace
              <br />
              to a working team.
            </h5>
            <p>
              Connect your tools. Start your first workflow. Give your team a
              simpler way to bring every customer on board.
            </p>
            <span className="cg-email-cta">Start your first workflow</span>
            <footer>
              You receive product updates from Northstar.
              <br />
              Manage preferences / Unsubscribe
            </footer>
          </article>
        )}
      </section>
    </div>
  );
}

export default function CompanyGrowthScenes({ mission, app, stage }) {
  if (app === "posthog")
    return <PostHogScene mission={mission} stage={stage} />;
  if (app === "googleads") return <GoogleAdsScene mission={mission} />;
  if (app === "mailchimp") return <MailchimpScene />;
  return null;
}
